import { zodResolver } from '@hookform/resolvers/zod';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DateTime } from 'luxon';
import { type FC, useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { useCreateShift, useUpdateShift } from '../../api/index.js';
import useSnackBar from '../../hooks/use-snack-bar.js';

export interface OverlapEvent {
  resourceId: string;
  start: Date;
  end: Date;
}

export interface ShiftCreateModalProps {
  open: boolean;
  onClose: () => void;
  prefill: { start: Date; end: Date; monitorId: string | null } | null;
  monitors: { id: string; name: string }[];
  currentEvents: OverlapEvent[];
  mode?: 'create' | 'edit';
  editTarget?: {
    occurrenceId: string;
    ruleId: string;
    isRecurring: boolean;
    start: Date;
    end: Date;
    monitorId: string;
  };
  onDelete?: () => void;
  onExited?: () => void;
}

const WEEKDAYS = [
  { value: 'MO', label: 'M', ariaLabel: 'Monday' },
  { value: 'TU', label: 'T', ariaLabel: 'Tuesday' },
  { value: 'WE', label: 'W', ariaLabel: 'Wednesday' },
  { value: 'TH', label: 'T', ariaLabel: 'Thursday' },
  { value: 'FR', label: 'F', ariaLabel: 'Friday' },
  { value: 'SA', label: 'S', ariaLabel: 'Saturday' },
  { value: 'SU', label: 'S', ariaLabel: 'Sunday' },
] as const;

const shiftCreateSchema = z.object({
  userId: z.string().min(1, 'Monitor is required.'),
  start: z.custom<DateTime>((val) => DateTime.isDateTime(val) && val.isValid, 'Start time is required.'),
  end: z.custom<DateTime>((val) => DateTime.isDateTime(val) && val.isValid, 'End time is required.'),
  repeats: z.boolean(),
  frequency: z.enum(['daily', 'weekly']),
  interval: z.number().int().min(1, 'Must be at least 1.'),
  byDay: z.array(z.enum(['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'])),
  until: z.custom<DateTime>((val) => val === null || (DateTime.isDateTime(val) && val.isValid)).nullable(),
}).refine((v) => {
  if (!DateTime.isDateTime(v.end) || !DateTime.isDateTime(v.start)) {
    return true;
  }
  return v.end > v.start;
}, {
  message: 'End time must be after start time.',
  path: ['end'],
}).refine((v) => {
  if (!v.repeats || v.frequency !== 'weekly') {
    return true;
  }
  return v.byDay.length >= 1;
}, {
  message: 'Select at least one day.',
  path: ['byDay'],
}).refine((v) => {
  if (!v.repeats) {
    return true;
  }
  return v.until !== null;
}, {
  message: 'End date is required.',
  path: ['until'],
}).refine((v) => {
  if (!v.repeats || v.until === null || !DateTime.isDateTime(v.until) || !DateTime.isDateTime(v.start)) {
    return true;
  }
  return v.until > v.start;
}, {
  message: 'End date must be after the shift start.',
  path: ['until'],
});

type ShiftCreateFormValues = z.infer<typeof shiftCreateSchema>;

const toPrefillDefaults = (prefill: ShiftCreateModalProps['prefill']): ShiftCreateFormValues => ({
  userId: prefill?.monitorId ?? '',
  start: prefill?.start ? DateTime.fromJSDate(prefill.start) : DateTime.now(),
  end: prefill?.end ? DateTime.fromJSDate(prefill.end) : DateTime.now().plus({ hours: 1 }),
  repeats: false,
  frequency: 'weekly',
  interval: 1,
  byDay: [],
  until: null,
});

const toEditDefaults = (target: NonNullable<ShiftCreateModalProps['editTarget']>): ShiftCreateFormValues => ({
  userId: target.monitorId,
  start: DateTime.fromJSDate(target.start),
  end: DateTime.fromJSDate(target.end),
  repeats: false,
  frequency: 'weekly',
  interval: 1,
  byDay: [],
  until: null,
});

const ShiftCreateModal: FC<ShiftCreateModalProps> = ({
  open,
  onClose,
  prefill,
  monitors,
  currentEvents,
  mode = 'create',
  editTarget,
  onDelete,
  onExited,
}) => {
  const snackBar = useSnackBar();
  const { mutateAsync: createMutate } = useCreateShift();
  const { mutateAsync: updateMutate } = useUpdateShift();
  const [editScope, setEditScope] = useState<'this' | 'all-future'>('this');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ShiftCreateFormValues>({
    resolver: zodResolver(shiftCreateSchema),
    defaultValues: toPrefillDefaults(prefill),
  });

  useEffect(() => {
    if (mode === 'edit' && editTarget) {
      reset(toEditDefaults(editTarget));
      setEditScope('this');
    } else {
      reset(toPrefillDefaults(prefill));
    }
  }, [mode, editTarget, prefill, reset]);

  const [watchedUserId, watchedStart, watchedEnd, repeats, frequency] = useWatch({
    control,
    name: ['userId', 'start', 'end', 'repeats', 'frequency'],
  });

  const hasOverlap = useMemo(() => {
    if (mode === 'edit' || !watchedUserId || !DateTime.isDateTime(watchedStart) || !DateTime.isDateTime(watchedEnd)) {
      return false;
    }
    const startDate = watchedStart.toJSDate();
    const endDate = watchedEnd.toJSDate();
    return currentEvents.some((event) => {
      if (event.resourceId !== watchedUserId) {
        return false;
      }
      return event.start < endDate && event.end > startDate;
    });
  }, [mode, watchedUserId, watchedStart, watchedEnd, currentEvents]);

  const onSubmitEdit = async (data: ShiftCreateFormValues) => {
    if (!editTarget) {
      return;
    }
    try {
      const scope = editTarget.isRecurring ? editScope : 'this';
      const result = await updateMutate({
        occurrenceId: editTarget.occurrenceId,
        userId: data.userId,
        start: data.start.toISO()!,
        end: data.end.toISO()!,
        scope,
      });
      snackBar.success(scope === 'all-future' ? 'Shift series updated.' : 'Shift updated.');
      if (result && typeof result === 'object' && 'overlapWarning' in result && result.overlapWarning) {
        snackBar.error('One or more occurrences overlap an existing shift for this monitor.');
      }
      onClose();
    } catch {
      snackBar.error('Failed to update shift. Please try again.');
    }
  };

  const onSubmitCreate = async (data: ShiftCreateFormValues) => {
    try {
      const basePayload = {
        userId: data.userId,
        start: data.start.toISO()!,
        end: data.end.toISO()!,
        repeats: false,
        byDay: [],
      };

      const payload = data.repeats
        ? {
            ...basePayload,
            repeats: true,
            freq: data.frequency === 'weekly' ? 2 : 3,
            interval: data.interval,
            byDay: data.byDay,
            until: data.until!.toISO()!,
          }
        : basePayload;

      const result = await createMutate(payload);
      snackBar.success('Shift created.');
      if (result.overlapWarning) {
        snackBar.error('One or more occurrences overlap an existing shift for this monitor.');
      }
      onClose();
    } catch {
      snackBar.error('Failed to save shift. Please try again.');
    }
  };

  const onSubmit = mode === 'edit' ? onSubmitEdit : onSubmitCreate;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited }}
      PaperProps={{
        component: 'form',
        sx: { minWidth: 480 },
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <DialogTitle>{mode === 'edit' ? 'Edit Shift' : 'New Shift'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Controller
            name="userId"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={monitors}
                getOptionLabel={(option) => option.name}
                value={monitors.find((m) => m.id === field.value) ?? null}
                onChange={(_, value) => field.onChange(value?.id ?? '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Monitor"
                    InputLabelProps={{ required: true }}
                    error={!!errors.userId}
                    helperText={errors.userId?.message ?? ''}
                  />
                )}
              />
            )}
          />
          <Controller
            name="start"
            control={control}
            render={({ field }) => (
              <DateTimePicker
                {...field}
                label="Start"
                slotProps={{
                  textField: {
                    InputLabelProps: { required: true },
                    error: !!errors.start,
                    helperText: errors.start?.message ?? '',
                    fullWidth: true,
                  },
                }}
              />
            )}
          />
          <Controller
            name="end"
            control={control}
            render={({ field }) => (
              <DateTimePicker
                {...field}
                label="End"
                minDateTime={DateTime.isDateTime(watchedStart) ? watchedStart : undefined}
                slotProps={{
                  textField: {
                    InputLabelProps: { required: true },
                    error: !!errors.end,
                    helperText: errors.end?.message ?? '',
                    fullWidth: true,
                  },
                }}
              />
            )}
          />
          {mode !== 'edit' && (
            <>
              <Controller
                name="repeats"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Repeats"
                    labelPlacement="end"
                  />
                )}
              />
              <Collapse in={repeats}>
                <Stack spacing={2}>
                  <Controller
                    name="frequency"
                    control={control}
                    render={({ field }) => (
                      <ToggleButtonGroup
                        exclusive
                        fullWidth
                        size="small"
                        aria-label="Frequency"
                        value={field.value}
                        onChange={(_, val) => { if (val !== null) { field.onChange(val); } }}
                      >
                        <ToggleButton value="daily">Daily</ToggleButton>
                        <ToggleButton value="weekly">Weekly</ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Controller
                      name="interval"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Every"
                          type="number"
                          sx={{ width: 80 }}
                          inputProps={{ min: 1, max: 99 }}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                          error={!!errors.interval}
                          helperText={errors.interval?.message ?? ''}
                        />
                      )}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {frequency === 'weekly' ? 'week(s)' : 'day(s)'}
                    </Typography>
                  </Stack>
                  <Collapse in={frequency === 'weekly'}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Repeat on
                      </Typography>
                      <Controller
                        name="byDay"
                        control={control}
                        render={({ field }) => (
                          <>
                            <ToggleButtonGroup
                              value={field.value}
                              onChange={(_, val) => field.onChange(val)}
                              aria-label="Repeat on"
                            >
                              {WEEKDAYS.map((day) => (
                                <ToggleButton
                                  key={day.value}
                                  value={day.value}
                                  aria-label={day.ariaLabel}
                                  sx={{ minWidth: 32, height: 32, p: 0 }}
                                >
                                  {day.label}
                                </ToggleButton>
                              ))}
                            </ToggleButtonGroup>
                            {errors.byDay && (
                              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                {errors.byDay.message ?? (errors.byDay as { root?: { message?: string } })?.root?.message ?? 'Select at least one day.'}
                              </Typography>
                            )}
                          </>
                        )}
                      />
                    </Box>
                  </Collapse>
                  <Controller
                    name="until"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        label="Ends on"
                        minDate={DateTime.isDateTime(watchedStart) ? watchedStart : undefined}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            InputLabelProps: { required: true },
                            error: !!errors.until,
                            helperText: errors.until?.message ?? '',
                          },
                        }}
                      />
                    )}
                  />
                </Stack>
              </Collapse>
            </>
          )}
          {mode === 'edit' && editTarget?.isRecurring && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Apply change to</Typography>
              <RadioGroup
                value={editScope}
                onChange={(_, v) => setEditScope(v as 'this' | 'all-future')}
                aria-label="Apply change to"
              >
                <FormControlLabel value="this" control={<Radio />} label="This occurrence" />
                <FormControlLabel value="all-future" control={<Radio />} label="This & all future" />
              </RadioGroup>
            </Box>
          )}
        </Stack>
      </DialogContent>
      {hasOverlap && (
        <Alert severity="warning" sx={{ mx: 3, mb: 1 }}>
          This monitor already has a shift during this time. You can still save.
        </Alert>
      )}
      {mode === 'edit' ? (
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button color="error" variant="text" startIcon={<DeleteOutlineIcon />} onClick={onDelete}>
            Delete shift
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose}>Cancel</Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={Object.keys(errors).length > 0}
            >
              Save changes
            </LoadingButton>
          </Box>
        </DialogActions>
      ) : (
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={Object.keys(errors).length > 0}
          >
            Save Shift
          </LoadingButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ShiftCreateModal;
